import {cva} from 'platform/shared';

import {CSSProperties, ReactNode} from 'react';

import {isNotNil} from 'ramda';

import {TestIdProps} from 'shared/web';

import {useResponsivePropValue} from '../../hooks/useResponsivePropValue';
import {CSSDimension} from '../../types/CSSDimension';
import {CSSGridTrackSize} from '../../types/CSSGridTrackSize';
import {FlexboxAlign} from '../../types/FlexboxAlign';
import {GridAutoFlow} from '../../types/GridAutoFlow';
import {GridTemplate} from '../../types/GridTemplate';
import {Integer} from '../../types/Integer';
import {ValueByDevice} from '../../types/ValueByDevice';
import {getCssSize} from '../../utils/getCssSize';
import {getSize} from '../../utils/getSize';
import {getGridTemplateColumns} from './getGridTemplateColumns';

const gridVariants = cva('grid');

interface GridBaseProps extends TestIdProps {
  spacing?: Integer | ValueByDevice<Integer>;
  grow?: number;
  verticalSpacing?: Integer | ValueByDevice<Integer>;
  horizontalSpacing?: Integer | ValueByDevice<Integer>;
  children?: ReactNode;
  minHeight?: Integer | CSSDimension | ValueByDevice<CSSDimension | Integer | undefined>;
  align?: FlexboxAlign;
  autoRows?: CSSGridTrackSize | ValueByDevice<CSSGridTrackSize>;
  autoFlow?: GridAutoFlow;
  /**
   * @about Explicit row tracks, built with `gridTracks`
   */
  rows?: GridTemplate | ValueByDevice<GridTemplate>;
  height?: CSSDimension | Integer | ValueByDevice<CSSDimension | Integer | undefined>;
}

interface GridFixedColumnsProps {
  /**
   * @about Number of equal columns or explicit tracks built with `gridTracks`
   */
  columns: number | GridTemplate | ValueByDevice<number | GridTemplate>;
  minColumnWidth?: never;
  maxColumns?: never;
  fill?: never;
}

interface GridAutoColumnsProps {
  columns?: never;
  /**
   * @about Minimum column width as a multiplier of the design system's base step.
   * Columns are added while they fit and stretch to share the remaining space.
   */
  minColumnWidth: Integer | ValueByDevice<Integer>;
  /**
   * @about Upper limit of columns, however wide the container is
   */
  maxColumns?: Exclude<Integer, 0>;
  /**
   * @about Keep empty tracks instead of collapsing them (`auto-fill` instead of `auto-fit`)
   */
  fill?: boolean;
}

export type GridProps = GridBaseProps & (GridFixedColumnsProps | GridAutoColumnsProps);

export function Grid(props: GridProps) {
  const columns = useResponsivePropValue(props.columns);
  const minColumnWidth = useResponsivePropValue(props.minColumnWidth);
  const rows = useResponsivePropValue(props.rows);
  const height = useResponsivePropValue(props.height);
  const spacing = useResponsivePropValue(props.spacing);
  const verticalSpacing = useResponsivePropValue(props.verticalSpacing);
  const horizontalSpacing = useResponsivePropValue(props.horizontalSpacing);
  const minHeight = useResponsivePropValue(props.minHeight);
  const autoRows = useResponsivePropValue(props.autoRows);

  const verticalGap = verticalSpacing ?? spacing ?? DEFAULT_GAP;
  const horizontalGap = horizontalSpacing ?? spacing ?? DEFAULT_GAP;
  const style: CSSProperties = {
    gridTemplateColumns: getGridTemplateColumns({
      columns,
      minColumnWidth,
      maxColumns: props.maxColumns,
      fill: props.fill,
      horizontalGap,
    }),
    gridTemplateRows: rows?.template,
    gridAutoRows: autoRows ?? 'initial',
    gridAutoFlow: props.autoFlow ?? 'initial',
    alignItems: props.align ?? 'initial',
    flexGrow: props.grow ?? 'initial',
    gap: `${getSize(verticalGap)} ${getSize(horizontalGap)}`,
    minWidth: 0,
    minHeight: isNotNil(minHeight) ? getCssSize(minHeight) : 0,
    height: getCssSize(height),
  };

  return (
    <div data-testid={props['data-testid']} className={gridVariants()} style={style}>
      {props.children}
    </div>
  );
}

const DEFAULT_GAP = 4;
